﻿/**
 * Events Class
 * Handles the event hooks for fragment lifecycle events and communication back to the host application.
 * This class is exposed to the consuming application for monitoring events.
 */

import type {
  EventFor,
  EventListener,
  EventListenerFor,
  EventPayloadByType,
  EventType,
} from '@anddone/ajs-util';
import { createLogger } from '@anddone/ajs-logger';

const logger = createLogger('SDK', 'Events');

export class Events {
  private readonly listeners = new Map<EventType, Set<EventListener>>();

  /**
   * Registers an event listener for the provided event type.
   *
   * Creates a new listener set when the event has not been registered before.
   * Registered listeners are invoked whenever the matching event is emitted.
   *
   * @param event - Event type to listen for.
   * @param listener - Callback function to invoke when the event is emitted.
   * @returns void
   */
  public on<Type extends EventType>(event: Type, listener: EventListenerFor<Type>): void {
    const listeners = this.getOrCreateListeners(event);

    listeners.add(listener as EventListener);

    logger.debug('listener', `Registered listener for "${event}"`, {
      event,
      listenerCount: this.listenerCount(event),
    });
  }

  /**
   * Removes an event listener for the provided event type.
   *
   * Removes the listener from the matching event listener set.
   * Deletes the listener set when no listeners remain for that event.
   *
   * @param event - Event type the listener is registered under.
   * @param listener - Callback function to remove.
   * @returns void
   */
  public off<Type extends EventType>(event: Type, listener: EventListenerFor<Type>): void {
    const listeners = this.listeners.get(event);

    if (!listeners) {
      logger.warn('listener', `No listeners found while removing listener for "${event}"`, {
        event,
      });

      return;
    }

    listeners.delete(listener as EventListener);

    logger.debug('listener', `Removed listener for "${event}"`, {
      event,
      listenerCount: listeners.size,
    });

    if (listeners.size === 0) {
      this.listeners.delete(event);

      logger.debug('listener', `Removed empty listener set for "${event}"`, {
        event,
      });
    }
  }

  /**
   * Registers an event listener that is invoked only once.
   *
   * Removes the wrapped listener before invoking the original listener.
   * This prevents the listener from being invoked again if the callback throws.
   *
   * @param event - Event type to listen for.
   * @param listener - Callback function to invoke once when the event is emitted.
   * @returns void
   */
  public once<Type extends EventType>(event: Type, listener: EventListenerFor<Type>): void {
    logger.debug('listener', `Registered one-time listener for "${event}"`, {
      event,
    });

    const onceWrapper: EventListenerFor<Type> = emittedEvent => {
      logger.debug('listener', `Executing one-time listener for "${event}"`, {
        event,
        emittedEvent,
      });

      this.off(event, onceWrapper);
      listener(emittedEvent);
    };

    this.on(event, onceWrapper);
  }

  /**
   * Emits an event to all registered listeners for the provided event type.
   *
   * Builds the typed event envelope before notifying listeners.
   *
   * @param event - Event type to emit.
   * @param data - Payload data for the emitted event.
   * @returns void
   */
  public emit<Type extends EventType>(event: Type, data: EventPayloadByType[Type]): void {
    const listeners = this.listeners.get(event);

    if (!listeners || listeners.size === 0) {
      logger.debug('emit', `No listeners registered for "${event}"`, {
        event,
        data,
      });

      return;
    }

    const emittedEvent: EventFor<Type> = {
      type: event,
      timestamp: new Date().toISOString(),
      data,
    };

    // Create snapshot of listeners to avoid issues if listeners mutate during execution.
    const listenersToNotify = Array.from(listeners) as EventListenerFor<Type>[];

    logger.debug('emit', `Emitting "${event}" to ${listenersToNotify.length} listener(s)`, {
      event,
      listenerCount: listenersToNotify.length,
      emittedEvent,
    });

    listenersToNotify.forEach(listener => {
      try {
        listener(emittedEvent);
      } catch (error) {
        logger.error('emit', `Error in event listener for "${event}"`, {
          event,
          emittedEvent,
          error,
        });
      }
    });
  }

  /**
   * Removes registered listeners for one event type or for all event types.
   *
   * When an event is provided, only listeners for that event are removed.
   * When no event is provided, all registered event listeners are removed.
   *
   * @param event - Optional event type whose listeners should be removed.
   * @returns void
   */
  public removeAllListeners(event?: EventType): void {
    if (event) {
      const count = this.listenerCount(event);

      this.listeners.delete(event);

      logger.info('listener', `Removed all listeners for "${event}"`, {
        event,
        removedCount: count,
      });

      return;
    }

    const eventNames = this.eventNames();
    const totalListenerCount = eventNames.reduce((total, eventName) => {
      return total + this.listenerCount(eventName);
    }, 0);

    this.listeners.clear();

    logger.info('listener', 'Removed all listeners for all events', {
      eventCount: eventNames.length,
      totalListenerCount,
      eventNames,
    });
  }

  /**
   * Gets the number of registered listeners for the provided event type.
   *
   * Returns zero when the event has no registered listener set.
   *
   * @param event - Event type whose listener count should be returned.
   * @returns Number of registered listeners for the event.
   */
  public listenerCount(event: EventType): number {
    return this.listeners.get(event)?.size ?? 0;
  }

  /**
   * Gets all event types with currently registered listeners.
   *
   * Returns only event types that currently have listener sets.
   *
   * @returns Array of registered event types.
   */
  public eventNames(): EventType[] {
    return Array.from(this.listeners.keys());
  }

  /**
   * Gets the listener set for an event type.
   *
   * Creates a new listener set when one does not already exist.
   *
   * @param event - Event type whose listener set should be returned.
   * @returns Listener set for the event type.
   */
  private getOrCreateListeners(event: EventType): Set<EventListener> {
    let listeners = this.listeners.get(event);

    if (!listeners) {
      listeners = new Set<EventListener>();
      this.listeners.set(event, listeners);

      logger.debug('listener', `Created listener set for "${event}"`, {
        event,
      });
    }

    return listeners;
  }
}
